import { type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Reusable empty-state block — an icon, a title, and a short
 * description. Used for the "no rows" and "filtered to nothing" states
 * a list must distinguish, and for the "nothing selected" idle state of
 * a detail pane.
 *
 * Keeping this one component means every empty surface in CORTEX has the
 * same visual weight and tone, rather than each screen inventing its own.
 */
export interface EmptyStateProps {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description?: string;
  readonly className?: string;
  /** Optional call-to-action (e.g. a Button) rendered under the text. */
  readonly action?: React.ReactNode;
}

export function EmptyState({ icon: Icon, title, description, className, action }: EmptyStateProps) {
  return (
    <div className={cn('px-6 py-10 text-center', className)}>
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Icon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      </div>
      <p className="text-xs font-semibold text-foreground">{title}</p>
      {description && <p className="mt-1 text-micro text-muted-foreground">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}
